import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

export interface PageHeaderProps extends ComponentPropsWithoutRef<"div"> {
  filters?: ReactNode;
  hasBorder?: boolean;
  /**
   * Fallback-only: rendered in the leftContent slot when `leftContent` is
   * not provided. If both are passed, `headline` is dropped. This differs
   * from V2's `StandardLayoutHeader`, which renders `headline` as a
   * sibling block below the main row.
   */
  headline?: ReactNode;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
}

export const PageHeader = ({
  leftContent,
  rightContent,
  filters,
  headline,
  hasBorder = true,
  className,
  ...rest
}: PageHeaderProps) => {
  const hasFilters = Boolean(filters);
  const hasHeadline = Boolean(headline);
  const { isViewOnlyMasquerading } = usePunditUserContext();

  return (
    <div
      {...rest}
      className={classNames(
        "bg-primary flex flex-col",
        hasBorder && "border-primary border-b",
        className,
      )}
    >
      {(leftContent || rightContent || hasHeadline) && (
        <div
          className={classNames(
            "flex min-h-16 items-center justify-between gap-4 px-6",
            { "!cursor-not-allowed": isViewOnlyMasquerading },
          )}
        >
          <div
            className={classNames("min-w-0", {
              "!pointer-events-none": isViewOnlyMasquerading,
            })}
          >
            {leftContent ?? headline}
          </div>
          {rightContent && (
            <div
              className={classNames("shrink-0", {
                "!pointer-events-none": isViewOnlyMasquerading,
              })}
            >
              {rightContent}
            </div>
          )}
        </div>
      )}
      {hasFilters && <div className="px-6 pb-4">{filters}</div>}
    </div>
  );
};
